import React, { useState, useEffect } from 'react';
import { Badge, Button, Dropdown } from 'react-bootstrap';
import NotificationService from '../services/NotificationService';
import { useNavigate } from 'react-router-dom';
import './NotificationList.css';

const NotificationList = ({ userId, onNotificationClick }) => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (userId) {
            loadNotifications();
        }
    }, [userId]);

    const loadNotifications = async () => {
        try {
            setLoading(true);
            const data = await NotificationService.getUnreadNotifications(userId, 10);
            setNotifications(data);
            setError(null);
        } catch (err) {
            console.error('Error loading notifications:', err);
            setError('Failed to load notifications');
        } finally {
            setLoading(false);
        }
    };

    const handleNotificationClick = async (notification) => {
        try {
            // Mark as read
            if (!notification.isRead) {
                await NotificationService.markAsRead(notification.id);
                // Update local state
                setNotifications(prev => 
                    prev.map(n => 
                        n.id === notification.id ? { ...n, isRead: true } : n
                    )
                );
            }

            // Navigate to the action URL if available
            if (notification.actionUrl) {
                navigate(notification.actionUrl);
            }

            // Call parent callback
            if (onNotificationClick) {
                onNotificationClick(notification);
            }
        } catch (error) {
            console.error('Error handling notification click:', error);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            await NotificationService.markAllAsRead(userId);
            setNotifications(prev => 
                prev.map(n => ({ ...n, isRead: true }))
            );
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };

    const handleDeleteNotification = async (notificationId, event) => {
        event.stopPropagation();
        try {
            await NotificationService.deleteNotification(notificationId);
            setNotifications(prev => 
                prev.filter(n => n.id !== notificationId)
            );
        } catch (error) {
            console.error('Error deleting notification:', error);
        }
    };

    if (loading) {
        return (
            <div className="notification-dropdown">
                <div className="notifications-header">
                    <h6>Notifications</h6>
                </div>
                <div className="notifications-content">
                    <div className="loading-notifications">Loading...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="notification-dropdown">
                <div className="notifications-header">
                    <h6>Notifications</h6>
                </div>
                <div className="notifications-content">
                    <div className="error-notifications">{error}</div>
                </div>
            </div>
        );
    }

    if (notifications.length === 0) {
        return (
            <div className="notification-dropdown">
                <div className="notifications-header">
                    <h6>Notifications</h6>
                </div>
                <div className="notifications-content">
                    <div className="no-notifications">
                        <p>No new notifications</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="notification-dropdown">
            <div className="notifications-header">
                <h6>Notifications ({notifications.length})</h6>
                <Button 
                    size="sm" 
                    variant="outline-primary"
                    onClick={handleMarkAllAsRead}
                    className="mark-all-read-btn"
                >
                    Mark All Read
                </Button>
            </div>
            <div className="notifications-content">
                {notifications.map((notification) => (
                    <div 
                        key={notification.id} 
                        className={`notification-item ${!notification.isRead ? 'unread' : ''}`}
                        onClick={() => handleNotificationClick(notification)}
                    >
                        <div className="notification-icon">
                            <span className="notification-emoji">
                                {NotificationService.getNotificationIcon(notification.notificationType)}
                            </span>
                        </div>
                        <div className="notification-content">
                            <div className="notification-message">
                                {notification.message}
                            </div>
                            <div className="notification-meta">
                                <span className="notification-time">
                                    {NotificationService.formatNotificationDate(notification.createdAt)}
                                </span>
                                <Badge 
                                    bg={NotificationService.getNotificationColor(notification.notificationType)}
                                    className="notification-type-badge"
                                >
                                    {notification.notificationType}
                                </Badge>
                            </div>
                        </div>
                        <div className="notification-actions">
                            <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={(e) => handleDeleteNotification(notification.id, e)}
                                className="delete-notification-btn"
                                title="Delete notification"
                            >
                                ×
                            </Button>
                        </div>
                    </div>
                ))}
            </div>
            <div className="notifications-footer">
                <Button 
                    variant="link" 
                    size="sm"
                    onClick={() => navigate('/user-dashboard/notifications')}
                    className="view-all-notifications-btn"
                >
                    View All Notifications
                </Button>
            </div>
        </div>
    );
};

export default NotificationList; 